import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import { formatDistanceToNow } from 'date-fns';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription, interval } from 'rxjs';
import { startWith, switchMap } from 'rxjs/operators';
import YukiFolder from 'src/app/models/yuki/YukiFolder';

import { YukiSettings } from '../../models/yuki/YukiSettings';
import { CreateNewInvoiceModalComponent } from '../create-new-invoice-modal/create-new-invoice-modal.component';
import { InvoicesSettingsModalComponent } from '../invoices-settings-modal/invoices-settings-modal.component';
import { FetchAllDocuments, FetchFolders, FetchYukiSettings, SelectFolder, SyncDocuments } from '../yuki.actions';
import { YukiService } from '../yuki.service';
import { YukiState } from '../yuki.state';

@Component({
    selector: 'app-yuki-overview',
    templateUrl: './yuki-overview.component.html',
    styleUrls: ['./yuki-overview.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class YukiOverviewComponent implements OnInit, OnDestroy {
    @Select(YukiState.folders) folders$: Observable<YukiFolder[]>;
    folders: YukiFolder[] = [];
    @Select(YukiState.currentFolder) currentFolder$: Observable<YukiFolder>;
    selectedFolder: YukiFolder = null;
    @Select(YukiState.yukiSettings) yukiSettings$: Observable<YukiSettings>;
    lastYukiSync: string;
    isSyncingDocuments = false;
    syncJustCompleted = false;
    isStoppingSync = false;
    currentSyncStatus: any = null;
    private subscriptions = new Subscription();
    private syncStatusSubscription: Subscription;
    private syncCompletedTimeout: any;
    private initialFolderFromQueryParam: number | null = null;

    constructor(
        private store: Store,
        private titleService: Title,
        private toastr: ToastrService,
        private modalService: NgbModal,
        private yukiService: YukiService,
        private route: ActivatedRoute,
        private router: Router,
    ) {}

    ngOnInit(): void {
        this.titleService.setTitle('Yuki');

        // Read folder from query param
        const folderParam = this.route.snapshot.queryParamMap.get('folder');
        if (folderParam) {
            const folderId = parseInt(folderParam, 10);
            if (!isNaN(folderId)) {
                this.initialFolderFromQueryParam = folderId;
            }
        }

        this.fetchData();
        this.resumeSyncStatusIfRunning();
    }

    selectFolder(folder: YukiFolder) {
        this.store.dispatch(new SelectFolder(folder.id));
        // Update URL with folder query param
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { folder: folder.id },
            queryParamsHandling: 'merge',
        });
    }

    ngOnDestroy(): void {
        this.stopSyncStatusPolling();
        this.subscriptions.unsubscribe();
        clearTimeout(this.syncCompletedTimeout);
    }

    syncDocuments() {
        if (this.isSyncingDocuments) {
            return;
        }

        this.isSyncingDocuments = true;
        this.currentSyncStatus = null;
        this.store.dispatch(new SyncDocuments()).subscribe({
            next: (result: any) => {
                this.toastr.info(result?.message || 'Yuki sync started in background. This can take a while.');
                this.startSyncStatusPolling();
            },
            error: () => {
                this.isSyncingDocuments = false;
                this.currentSyncStatus = null;
                this.toastr.error('Oops, something went wrong, Please try again later...');
            },
        });
    }

    stopSyncDocuments() {
        if (!this.isSyncingDocuments || this.isStoppingSync) {
            return;
        }

        this.isStoppingSync = true;
        this.yukiService.stopSync().subscribe({
            next: (status: any) => {
                this.currentSyncStatus = status;
                this.toastr.info('Stopping AI auto-allocation after current document...');
                this.isStoppingSync = false;
            },
            error: () => {
                this.isStoppingSync = false;
                this.toastr.error('Could not stop AI auto-allocation.');
            },
        });
    }

    get syncPhaseLabel(): string {
        if (!this.currentSyncStatus) {
            return 'Starting sync...';
        }
        switch (this.currentSyncStatus.phase) {
            case 'FETCHING':
                return 'Fetching documents from Yuki...';
            case 'AI_PROCESSING':
                return 'AI auto-allocation in progress...';
            case 'COMPLETED':
                return 'Sync completed!';
            case 'STOPPED':
                return 'AI auto-allocation stopped';
            case 'FAILED':
                return 'Sync failed';
            default:
                return 'Syncing...';
        }
    }

    get syncProgressPercent(): number {
        const total = this.currentSyncStatus?.totalDocuments ?? 0;
        const processed = this.currentSyncStatus?.processedDocuments ?? 0;
        if (total === 0) {
            return 0;
        }
        return Math.round((processed / total) * 100);
    }

    get formattedLastSkipReason(): string {
        const reason = this.currentSyncStatus?.lastSkipReason;
        if (!reason) {
            return '';
        }

        if (reason.includes('has not changed since then')) {
            return 'already processed earlier and unchanged';
        }

        if (reason.includes('already has linked objects')) {
            return 'already linked';
        }

        if (reason.includes('already verified')) {
            return 'already verified';
        }

        if (reason.includes('not eligible for AI')) {
            return 'folder not eligible (only Purchase & Sales)';
        }

        return reason;
    }

    private startSyncStatusPolling() {
        this.stopSyncStatusPolling();
        this.syncStatusSubscription = interval(5000)
            .pipe(
                startWith(0),
                switchMap(() => this.yukiService.getSyncStatus()),
            )
            .subscribe({
                next: (status: any) => {
                    this.currentSyncStatus = status;
                    if (status?.running) {
                        return;
                    }

                    this.stopSyncStatusPolling();
                    this.isSyncingDocuments = false;
                    this.isStoppingSync = false;

                    const failedDocuments = status?.failedDocuments ?? 0;
                    const processedDocuments = status?.processedDocuments ?? 0;
                    const totalDocuments = status?.totalDocuments ?? 0;
                    const skippedDocuments = status?.skippedDocuments ?? 0;

                    if (failedDocuments > 0) {
                        this.toastr.warning(`Yuki sync completed with ${failedDocuments} failures (${processedDocuments}/${totalDocuments} processed).`);
                    } else {
                        const skippedMsg = skippedDocuments > 0 ? `, ${skippedDocuments} skipped` : '';
                        this.toastr.success(`Yuki sync completed (${processedDocuments}/${totalDocuments} processed${skippedMsg}).`);
                    }

                    // Keep the panel visible briefly after completion
                    this.syncJustCompleted = true;
                    clearTimeout(this.syncCompletedTimeout);
                    this.syncCompletedTimeout = setTimeout(() => {
                        this.syncJustCompleted = false;
                        this.currentSyncStatus = null;
                    }, 5000);

                    this.store.dispatch(new FetchYukiSettings());
                    if (this.selectedFolder?.id) {
                        this.store.dispatch(new FetchAllDocuments(this.selectedFolder.id));
                    }
                },
                error: () => {
                    this.stopSyncStatusPolling();
                    this.isSyncingDocuments = false;
                    this.isStoppingSync = false;
                    this.currentSyncStatus = null;
                    this.toastr.error('Could not fetch Yuki sync status.');
                },
            });
    }

    private stopSyncStatusPolling() {
        if (this.syncStatusSubscription) {
            this.syncStatusSubscription.unsubscribe();
            this.syncStatusSubscription = null;
        }
        if (!this.isSyncingDocuments) {
            this.currentSyncStatus = null;
        }
    }

    private resumeSyncStatusIfRunning() {
        this.yukiService.getSyncStatus().subscribe({
            next: (status: any) => {
                if (!status) {
                    return;
                }

                this.currentSyncStatus = status;
                if (status.running) {
                    this.isSyncingDocuments = true;
                    this.startSyncStatusPolling();
                    return;
                }

                if (status.phase === 'COMPLETED' || status.phase === 'FAILED' || status.phase === 'STOPPED') {
                    this.syncJustCompleted = true;
                    clearTimeout(this.syncCompletedTimeout);
                    this.syncCompletedTimeout = setTimeout(() => {
                        this.syncJustCompleted = false;
                        this.currentSyncStatus = null;
                    }, 5000);
                }
            },
            error: () => {
                this.isSyncingDocuments = false;
                this.currentSyncStatus = null;
            },
        });
    }

    openYukiSettingsModal() {
        this.modalService.open(InvoicesSettingsModalComponent, {
            windowClass: 'modal-pane',
            animation: false,
        });
    }

    openCreateInvoiceModal() {
        const modalRef = this.modalService.open(CreateNewInvoiceModalComponent, { windowClass: 'modal-pane', animation: false });
        modalRef.componentInstance.currentFolderId = this.selectedFolder.id;
    }

    private fetchData() {
        this.subscriptions.add(
            this.folders$.subscribe((folders) => {
                if (!folders) {
                    this.store.dispatch(new FetchFolders());
                    return;
                }

                if (folders) {
                    this.folders = folders;
                    if (!this.selectedFolder && this.folders.length > 0) {
                        // Use folder from query param if available, otherwise default to 'aankoop'
                        let targetFolder: YukiFolder | undefined;
                        if (this.initialFolderFromQueryParam !== null) {
                            targetFolder = this.folders.find((folder) => folder.id === this.initialFolderFromQueryParam);
                        }
                        if (!targetFolder) {
                            targetFolder = this.folders.find((folder) => folder.description.toLowerCase() === 'aankoop');
                        }
                        if (targetFolder) {
                            this.selectFolder(targetFolder);
                        }
                    }
                }
            }),
        );

        this.subscriptions.add(
            this.currentFolder$.subscribe((folder) => {
                this.selectedFolder = folder;
            }),
        );

        this.subscriptions.add(
            this.yukiSettings$.subscribe((settings) => {
                if (!settings) {
                    this.store.dispatch(new FetchYukiSettings());
                    return;
                }
                this.lastYukiSync = formatDistanceToNow(new Date(settings.lastSync));
            }),
        );
    }
}
